import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import { RTM } from "@/qrtc_web_rtm/rtm";
import { RTMConfig } from "@/qrtc_web_rtm/rtmTypes";
import * as consts from "@/config/const";
import { storage } from "@/util";

interface RTMContextProps {
  isRTMConnected: boolean;
  isRTMKicked: boolean;
  connectRTM: () => void;
  disconnectRTM: () => void;
  rtmPublishToPeer: (peerId: string, message: string) => void;
  rtmPublish: (channel: string, message: string) => void;
  addEventListener: (type: string, listener: (data: string) => void) => void;
  removeEventListener: (type: string, listener: (data: string) => void) => void;
}

interface RtmConstProp {
    appId?: string;
    userId?: string;
    rtmConfig?: RTMConfig;
  }
  
  const artmConfig = {
    serverUrl: consts.RTM_SERVERURL, //should add serverUrl in RTMConfig this is differ from agora
    token: consts.RTM_TOKEN,
    encryptionMode: "chacha20",
    cipherKey: "string",
    salt: new Uint8Array(6),
    useStringUserId: true,
    presenceTimeout: 1000,
    logUpload: false,
    logLevel: "verbose",
    cloudProxy: false,
  };

const RTMContext = createContext<RTMContextProps | undefined>(undefined);

interface RTMProviderProps {
  children: ReactNode;
}

export const RTMProvider: React.FC<RTMProviderProps> = ({ children }) => {
  const [isRTMConnected, setIsRTMConnected] = useState(false);
  const [isRTMKicked, setIsRTMKicked] = useState(false);
  const [rtmInstance, setRtmInstance] = useState<RTM | null>(null);

  const connectRTM = async () => {
    //const websocket = new WebSocket('wss://example.com/socket');
    const logInfo = storage.get(consts.LOGININFOSTORE) ?? {};
    const {
      appId = consts.RTM_APPID,
      userId = logInfo.userId,
      rtmConfig = artmConfig,
    } = {};
    if (!userId) {
      //console.log("not logined ...");
      return;
    }
    const inst = new RTM(appId, userId, rtmConfig);
    await inst.login();
    await inst.subscribe("notification");
    setRtmInstance(inst);
    setIsRTMConnected(true);
    setIsRTMKicked(false);
    inst?.on('closed', () => {
      console.log('Disconnected from RTM');
      setIsRTMConnected(false);
    });
    inst?.on('closedByServer', () => {
      console.log('Disconnected from RTM');
      setIsRTMKicked(true);
    });
  };

  const disconnectRTM = async () => {
    if (rtmInstance) {
      rtmInstance.close();
      setRtmInstance(null);
      setIsRTMConnected(false);
    }
  }

  const addEventListener = (type: string, listener: (data: string) => void) => {
    rtmInstance?.addEventListener(type, listener);
  }

  const removeEventListener = (type: string, listener: (data: string) => void) => {
    rtmInstance?.removeEventListener(type, listener);
  }

  const rtmPublishToPeer = async (peerId: string, message: string)=>{
    await rtmInstance?.publishToPeer(peerId, message);
  }

  const rtmPublish = async (channel: string, message: string)=>{
    await rtmInstance?.publish(channel, message);
  
  }

  useEffect(() => {
    return () => {
      if (rtmInstance) {
        rtmInstance.close();
      }
    };
  }, [rtmInstance]);

  return (
    <RTMContext.Provider value={{ 
        isRTMConnected, 
        isRTMKicked, 
        connectRTM, 
        disconnectRTM,
        rtmPublishToPeer,
        rtmPublish,
        addEventListener,
        removeEventListener }}>
      {children}
    </RTMContext.Provider>
  );
};

export const useRTM = (): RTMContextProps => {
  const context = useContext(RTMContext);
  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
};